import type { LayoutElement } from '../types';
import {
  ALL_RAIL_TYPES,
  ENDPOINT_TOLERANCE,
  getCircleJunctionTurnCenterlineNodes,
  getCurveRadius,
  getRailCurveCenterlineNodes,
  getRailEndpoints,
  isCurveRailType,
  resolveRailType,
  type Pt,
} from './topology';
import { buildTrackGraph, type TrackEdge, type TrackGraph } from './simulationGraph';
import {
  railIdFromSegmentId,
  resolveLayoutElementDisplayName,
} from './layoutElementDisplayName';

export type SimulationTrackNodeView = {
  nodeId: string;
  x: number;
  y: number;
  isHoldingPoint: boolean;
  segmentIds: string[];
};

export type RailGeometryNodeMarker = {
  railId: string;
  x: number;
  y: number;
  role: 'straight-endpoint' | 'curve-node';
  /** Index along the rail's centerline polyline (0 = start). */
  index: number;
};

/**
 * All centerline sample points for visible rails — straights use endpoints;
 * curves use the full {@link getRailCurveCenterlineNodes} polyline (3 or 4 nodes).
 * Simulation graph nodes only cluster segment ends, so interior curve nodes need
 * this separate list for canvas display.
 */
function isNearAny(pt: Pt, others: Pt[], tol2: number): boolean {
  for (const o of others) {
    const dx = o.x - pt.x;
    const dy = o.y - pt.y;
    if (dx * dx + dy * dy <= tol2) return true;
  }
  return false;
}

function snapCurveEndpointsToJunctionTips(
  nodes: Pt[],
  junctions: LayoutElement[],
  tolerance: number
): Pt[] {
  if (nodes.length < 2 || junctions.length === 0) return nodes;
  const tips: Pt[] = [];
  for (const junction of junctions) {
    for (const tip of getRailEndpoints(junction)) tips.push(tip);
  }
  const out = nodes.map((p) => ({ ...p }));
  for (const idx of [0, out.length - 1]) {
    let best = out[idx];
    let bestD = tolerance;
    for (const tip of tips) {
      const d = Math.hypot(tip.x - out[idx].x, tip.y - out[idx].y);
      if (d < bestD) {
        bestD = d;
        best = { x: tip.x, y: tip.y };
      }
    }
    out[idx] = best;
  }
  return out;
}

export function buildRailGeometryNodeMarkers(
  layoutElements: LayoutElement[]
): RailGeometryNodeMarker[] {
  const markers: RailGeometryNodeMarker[] = [];
  const curveJunctionPts: Pt[] = [];
  const tol2 = ENDPOINT_TOLERANCE * ENDPOINT_TOLERANCE;
  const visible = layoutElements.filter(
    (el) => ALL_RAIL_TYPES.has(el.type) && el.metadata.visible
  );
  const junctions = visible.filter((el) => resolveRailType(el) === 'junction-circle');
  const curveRadii = visible
    .filter((el) => isCurveRailType(resolveRailType(el)))
    .map((el) => getCurveRadius(el.dimensions.width, el.dimensions.height));
  const junctionSnapTol = Math.max(
    ENDPOINT_TOLERANCE,
    ...(curveRadii.length > 0 ? curveRadii : [ENDPOINT_TOLERANCE])
  );

  for (const el of visible) {
    const railType = resolveRailType(el);
    if (!isCurveRailType(railType)) continue;

    const nodes = snapCurveEndpointsToJunctionTips(
      getRailCurveCenterlineNodes(el),
      junctions,
      junctionSnapTol
    );
    nodes.forEach((pt, index) => {
      markers.push({ railId: el.id, x: pt.x, y: pt.y, role: 'curve-node', index });
    });
    if (nodes.length >= 2) {
      curveJunctionPts.push(nodes[0], nodes[nodes.length - 1]);
    }
  }

  for (const el of junctions) {
    const nodes = getCircleJunctionTurnCenterlineNodes(el, visible);
    nodes.forEach((pt, index) => {
      markers.push({ railId: el.id, x: pt.x, y: pt.y, role: 'curve-node', index });
    });
    if (nodes.length >= 2) {
      curveJunctionPts.push(nodes[0], nodes[nodes.length - 1]);
    }
  }

  for (const el of visible) {
    const railType = resolveRailType(el);
    if (isCurveRailType(railType) || railType === 'junction-circle' || railType === 'connector') {
      continue;
    }

    getRailEndpoints(el).forEach((pt, index) => {
      // Straights meeting a curve share endpoints with the curve polyline — show
      // only the four evenly spaced curve nodes so spacing looks consistent.
      if (isNearAny(pt, curveJunctionPts, tol2)) return;
      markers.push({
        railId: el.id,
        x: pt.x,
        y: pt.y,
        role: 'straight-endpoint',
        index,
      });
    });
  }

  return markers;
}

export type ExportableTrackEdge = {
  edge: TrackEdge;
  segmentId: string;
};

/** Backend track node id for a clustered graph node index (1-based). */
export function simulationTrackNodeId(index: number): string {
  return `NODE-${index + 1}`;
}

/** Curve/self-loop graph edges are kept for canvas routing but omitted from AMHS export. */
export function isExportableSimulationEdge(
  edge: TrackEdge,
  elementsById: Map<string, LayoutElement>
): boolean {
  if (edge.a === edge.b) return false;
  const rail = elementsById.get(edge.id);
  if (!rail) return true;
  if (rail.type === 'overhead-curve') return false;
  return !isCurveRailType(resolveRailType(rail));
}

function segmentIdForOccurrence(railId: string, occurrence: number, total: number): string {
  return total === 1 ? railId : `${railId}:${occurrence}`;
}

/** Track edges and ids included in the simulation API payload. */
export function getExportableTrackEdges(
  graph: TrackGraph,
  layoutElements: LayoutElement[] = []
): ExportableTrackEdge[] {
  const elementsById = new Map(layoutElements.map((element) => [element.id, element]));
  const edges = graph.edges.filter((edge) => isExportableSimulationEdge(edge, elementsById));
  const totals = new Map<string, number>();
  edges.forEach((edge) => totals.set(edge.id, (totals.get(edge.id) ?? 0) + 1));
  const occurrences = new Map<string, number>();

  return edges.map((edge) => {
    const total = totals.get(edge.id) ?? 1;
    const occurrence = occurrences.get(edge.id) ?? 0;
    occurrences.set(edge.id, occurrence + 1);
    return {
      edge,
      segmentId: segmentIdForOccurrence(edge.id, occurrence, total),
    };
  });
}

/** @deprecated Prefer getExportableTrackEdges for simulation API/UI segment ids. */
export function simulationSegmentIds(graph: TrackGraph): string[] {
  return getExportableTrackEdges(graph).map(({ segmentId }) => segmentId);
}

export function buildSimulationTrackNodeViews(
  layoutElements: LayoutElement[],
  holdingNodeIds: Set<string> = new Set()
): SimulationTrackNodeView[] {
  const graph = buildTrackGraph(layoutElements);
  if (graph.nodes.length === 0) return [];

  const exportable = getExportableTrackEdges(graph, layoutElements);
  const segmentIdsByNode = graph.nodes.map(() => [] as string[]);
  exportable.forEach(({ edge, segmentId }) => {
    segmentIdsByNode[edge.a].push(segmentId);
    if (edge.b !== edge.a) segmentIdsByNode[edge.b].push(segmentId);
  });

  return graph.nodes.map((node, index) => {
    const id = simulationTrackNodeId(index);
    return {
      nodeId: id,
      x: node.x,
      y: node.y,
      isHoldingPoint: holdingNodeIds.has(id),
      segmentIds: [...new Set(segmentIdsByNode[index])],
    };
  });
}

/** Map layout rail element ids to the simulation segment id(s) they produce. */
export function buildSimulationSegmentIdsByRail(
  layoutElements: LayoutElement[]
): Map<string, string[]> {
  const graph = buildTrackGraph(layoutElements);
  const byRail = new Map<string, string[]>();
  getExportableTrackEdges(graph, layoutElements).forEach(({ edge, segmentId }) => {
    const list = byRail.get(edge.id) ?? [];
    list.push(segmentId);
    byRail.set(edge.id, list);
  });
  return byRail;
}

/** Map simulation segment ids to user-facing track names from the current layout. */
export function buildSegmentDisplayNameMap(
  layoutElements: LayoutElement[],
  elementsById: Record<string, LayoutElement>,
  elementOrder: string[]
): Map<string, string> {
  const graph = buildTrackGraph(layoutElements);
  const map = new Map<string, string>();

  getExportableTrackEdges(graph, layoutElements).forEach(({ segmentId, edge }) => {
    const rail = elementsById[edge.id];
    const base = rail
      ? resolveLayoutElementDisplayName(rail, elementsById, elementOrder)
      : edge.id;
    if (segmentId === edge.id) {
      map.set(segmentId, base);
      return;
    }
    const suffix = segmentId.slice(segmentId.lastIndexOf(':') + 1);
    map.set(segmentId, `${base} #${Number(suffix) + 1}`);
  });

  return map;
}

export function resolveSegmentDisplayName(
  segmentId: string,
  nameMap: Map<string, string>,
  elementsById: Record<string, LayoutElement> = {},
  elementOrder: string[] = []
): string {
  const mapped = nameMap.get(segmentId);
  if (mapped) return mapped;

  const railId = railIdFromSegmentId(segmentId);
  const rail = elementsById[railId];
  if (rail) {
    return resolveLayoutElementDisplayName(rail, elementsById, elementOrder);
  }
  return segmentId;
}

export function formatSegmentDisplayNames(
  segmentIds: string[],
  nameMap: Map<string, string>,
  separator = ', ',
  elementsById: Record<string, LayoutElement> = {},
  elementOrder: string[] = []
): string {
  if (segmentIds.length === 0) return 'No connected segments';
  return segmentIds
    .map((segmentId) =>
      resolveSegmentDisplayName(segmentId, nameMap, elementsById, elementOrder)
    )
    .join(separator);
}
